import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { CheckInTemplate, CheckInQuestion } from "@/hooks/use-check-in-templates";

export interface CheckInAssignment {
  id: string;
  template_id: string;
  client_id: string;
  organization_id: string;
  frequency: "one_time" | "daily" | "weekly" | "biweekly" | "monthly";
  day_of_week: number | null;
  day_of_month: number | null;
  start_date: string;
  end_date: string | null;
  is_active: boolean | null;
  trainer_notes: string | null;
  send_reminder: boolean | null;
  next_due_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  template?: CheckInTemplate;
}

export interface CheckInResponse {
  id: string;
  assignment_id: string;
  client_id: string;
  response_date: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  trainer_feedback: string | null;
  flagged: boolean | null;
  flag_reason: string | null;
  answers?: CheckInAnswer[];
}

export interface CheckInAnswer {
  id: string;
  response_id: string;
  question_id: string;
  answer_text: string | null;
  answer_number: number | null;
  question?: CheckInQuestion;
}

export interface DueCheckIn {
  assignment_id: string;
  template_id: string;
  template_name: string;
  template_description: string | null;
  template_category: string;
  frequency: string;
  day_of_week: number | null;
  day_of_month: number | null;
  last_response_date: string | null;
  is_due_today: boolean;
  is_overdue: boolean;
  question_count: number;
}

export interface AssignCheckInInput {
  template_id: string;
  client_id: string;
  frequency?: "one_time" | "daily" | "weekly" | "biweekly" | "monthly";
  day_of_week?: number;
  day_of_month?: number;
  start_date?: string;
  end_date?: string;
  trainer_notes?: string;
  send_reminder?: boolean;
}

/**
 * Compute the next_due_at timestamp for a check-in assignment.
 * For one-time assignments, returns the start date.
 * For recurring, returns the first occurrence on or after the start date.
 */
export function computeNextDueAt(
  frequency: string,
  startDate: string,
  dayOfWeek?: number,
  dayOfMonth?: number
): string | null {
  const start = new Date(startDate + "T06:00:00Z"); // 6 AM UTC default

  if (frequency === "one_time") {
    return start.toISOString();
  }

  if (frequency === "daily") {
    return start.toISOString();
  }

  if (frequency === "weekly" || frequency === "biweekly") {
    const dow = dayOfWeek ?? 1; // Monday default
    const currentDow = start.getUTCDay();
    let daysUntil = (dow - currentDow + 7) % 7;
    if (daysUntil === 0) daysUntil = 0; // Same day is fine for start
    const next = new Date(start.getTime() + daysUntil * 24 * 60 * 60 * 1000);
    next.setUTCHours(6, 0, 0, 0);
    return next.toISOString();
  }

  if (frequency === "monthly") {
    const dom = Math.min(dayOfMonth ?? 1, 28);
    const next = new Date(start);
    next.setUTCDate(dom);
    next.setUTCHours(6, 0, 0, 0);
    if (next < start) {
      next.setUTCMonth(next.getUTCMonth() + 1);
      next.setUTCDate(dom);
    }
    return next.toISOString();
  }

  return start.toISOString();
}

export function useClientCheckIns(clientId: string | undefined) {
  const { organization } = useOrganization();
  const [assignments, setAssignments] = useState<CheckInAssignment[]>([]);
  const [responses, setResponses] = useState<CheckInResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAssignments = useCallback(async () => {
    if (!clientId || !organization?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("check_in_assignments")
        .select(`
          *,
          template:check_in_templates (
            id,
            name,
            description,
            category,
            is_system_template
          )
        `)
        .eq("client_id", clientId)
        .eq("organization_id", organization.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAssignments(data || []);
    } catch (error) {
      console.error("Error fetching client check-in assignments:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId, organization?.id]);

  const fetchResponses = useCallback(async (limit = 20) => {
    if (!clientId) return;

    try {
      const { data, error } = await supabase
        .from("check_in_responses")
        .select(`
          *,
          assignment:check_in_assignments (
            template:check_in_templates (name, category)
          )
        `)
        .eq("client_id", clientId)
        .order("submitted_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      setResponses(data || []);
    } catch (error) {
      console.error("Error fetching check-in responses:", error);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAssignments();
    fetchResponses();
  }, [fetchAssignments, fetchResponses]);

  // Real-time subscription - trainers see client check-in submissions live
  useEffect(() => {
    if (!clientId) return;

    const channel = supabase
      .channel(`check_in_responses_${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'check_in_responses',
          filter: `client_id=eq.${clientId}`,
        },
        () => {
          fetchResponses();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId, fetchResponses]);

  const assignCheckIn = async (input: AssignCheckInInput): Promise<CheckInAssignment | null> => {
    if (!organization?.id) return null;

    try {
      const freq = input.frequency || "weekly";
      const startDateStr = input.start_date || new Date().toISOString().split("T")[0];

      // Compute next_due_at based on frequency
      const nextDueAt = computeNextDueAt(
        freq,
        startDateStr,
        input.day_of_week,
        input.day_of_month
      );

      const { data, error } = await supabase
        .from("check_in_assignments")
        .insert({
          template_id: input.template_id,
          client_id: input.client_id,
          organization_id: organization.id,
          frequency: freq,
          day_of_week: input.day_of_week ?? (freq === "weekly" || freq === "biweekly" ? 1 : null),
          day_of_month: input.day_of_month ?? null,
          start_date: startDateStr,
          end_date: input.end_date || null,
          trainer_notes: input.trainer_notes || null,
          send_reminder: input.send_reminder ?? true,
          is_active: true,
          next_due_at: nextDueAt,
        })
        .select(`
          *,
          template:check_in_templates (
            id,
            name,
            description,
            category,
            is_system_template
          )
        `)
        .single();

      if (error) throw error;

      await fetchAssignments();
      return data;
    } catch (error) {
      console.error("Error assigning check-in:", error);
      throw error;
    }
  };

  const updateAssignment = async (
    assignmentId: string,
    updates: Partial<Omit<AssignCheckInInput, "template_id" | "client_id">>
  ): Promise<CheckInAssignment | null> => {
    try {
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.frequency !== undefined) updateData.frequency = updates.frequency;
      if (updates.day_of_week !== undefined) updateData.day_of_week = updates.day_of_week;
      if (updates.day_of_month !== undefined) updateData.day_of_month = updates.day_of_month;
      if (updates.start_date !== undefined) updateData.start_date = updates.start_date;
      if (updates.end_date !== undefined) updateData.end_date = updates.end_date;
      if (updates.trainer_notes !== undefined) updateData.trainer_notes = updates.trainer_notes;
      if (updates.send_reminder !== undefined) updateData.send_reminder = updates.send_reminder;

      const { data, error } = await supabase
        .from("check_in_assignments")
        .update(updateData)
        .eq("id", assignmentId)
        .select(`
          *,
          template:check_in_templates (
            id,
            name,
            description,
            category,
            is_system_template
          )
        `)
        .single();

      if (error) throw error;

      await fetchAssignments();
      return data;
    } catch (error) {
      console.error("Error updating check-in assignment:", error);
      throw error;
    }
  };

  const unassignCheckIn = async (assignmentId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("check_in_assignments")
        .update({ is_active: false })
        .eq("id", assignmentId);

      if (error) throw error;

      await fetchAssignments();
      return true;
    } catch (error) {
      console.error("Error unassigning check-in:", error);
      throw error;
    }
  };

  const getResponseWithAnswers = async (responseId: string): Promise<CheckInResponse | null> => {
    try {
      const { data: response, error: responseError } = await supabase
        .from("check_in_responses")
        .select("*")
        .eq("id", responseId)
        .single();

      if (responseError) throw responseError;

      const { data: answers, error: answersError } = await supabase
        .from("check_in_answers")
        .select(`
          *,
          question:check_in_questions (
            question_text,
            question_type,
            order_index,
            scale_min,
            scale_max,
            scale_labels,
            options
          )
        `)
        .eq("response_id", responseId);

      if (answersError) throw answersError;

      // Sort answers by question order
      const sortedAnswers = (answers || []).sort(
        (a: any, b: any) => (a.question?.order_index || 0) - (b.question?.order_index || 0)
      );

      return {
        ...response,
        answers: sortedAnswers,
      };
    } catch (error) {
      console.error("Error fetching response with answers:", error);
      return null;
    }
  };

  const updateResponse = async (
    responseId: string,
    updates: { trainer_feedback?: string; flagged?: boolean; flag_reason?: string }
  ): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const updateData: Record<string, any> = {};
      if (updates.trainer_feedback !== undefined) updateData.trainer_feedback = updates.trainer_feedback;
      if (updates.flagged !== undefined) updateData.flagged = updates.flagged;
      if (updates.flag_reason !== undefined) updateData.flag_reason = updates.flag_reason;

      // Mark as reviewed if adding feedback
      if (updates.trainer_feedback) {
        updateData.reviewed_at = new Date().toISOString();
        updateData.reviewed_by = user?.id;
      }

      const { error } = await supabase
        .from("check_in_responses")
        .update(updateData)
        .eq("id", responseId);

      if (error) throw error;

      await fetchResponses();
      return true;
    } catch (error) {
      console.error("Error updating response:", error);
      throw error;
    }
  };

  return {
    assignments,
    responses,
    loading,
    refetch: fetchAssignments,
    refetchResponses: fetchResponses,
    assignCheckIn,
    updateAssignment,
    unassignCheckIn,
    getResponseWithAnswers,
    updateResponse,
  };
}

// Hook to get compliance stats for a client
export function useCheckInCompliance(clientId: string | undefined, days = 30) {
  const [compliance, setCompliance] = useState<{
    total_expected: number;
    total_completed: number;
    compliance_rate: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    const fetchCompliance = async () => {
      try {
        const { data, error } = await supabase.rpc("get_checkin_compliance", {
          p_client_id: clientId,
          p_days: days,
        });

        if (error) throw error;
        if (data && data.length > 0) {
          setCompliance(data[0]);
        }
      } catch (error) {
        console.error("Error fetching check-in compliance:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompliance();
  }, [clientId, days]);

  return { compliance, loading };
}

// Frequency display helper
export const FREQUENCY_DISPLAY: Record<string, { label: string; description: string }> = {
  one_time: { label: "One-time", description: "Single check-in" },
  daily: { label: "Daily", description: "Every day" },
  weekly: { label: "Weekly", description: "Once per week" },
  biweekly: { label: "Bi-weekly", description: "Every 2 weeks" },
  monthly: { label: "Monthly", description: "Once per month" },
};
